/* eslint-disable @next/next/no-before-interactive-script-outside-document */
// Polyfill File for SSR (required for Chakra v3 / @zag-js compatibility)
import "@/lib/polyfills/file-polyfill";

// Emotion cache for SSR
import type { EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "@/lib/createEmotionCache";

// CSS imports - order matters for cascade layers
import "@/styles/grecaptcha.css";
import "@/styles/nprogress.css";
import "@ui-kit/form/DatePicker/DatePicker.style.css";
import "cropperjs/dist/cropper.css";
import "react-day-picker/src/style.css";
import "react-quill/dist/quill.snow.css";
import "swiper/swiper-bundle.min.css";
import "@shared/theme/styles/globals.css";

import Template from "@ui-application/layout/Template/Template.web";
import Footer from "@ui-application/navigation/footer/Footer/Footer.web";
import NavBar from "@ui-application/navigation/NavBar/NavBar.web";

import { UserSessionAuthStateProvider } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { DesktopAgentProvider } from "@shared/contexts/DesktopAgentContext/DesktopAgentContext";

import { API_URL, SITE_URL, STATIC_FILE_PATH } from "@app/core/src/shared/env";
import styled from "@emotion/styled";
import { AnalyticsContextProvider } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import { useAndroidTWACheck } from "@shared/hooks/platform/useAndroidTWACheck";
import "@shared/lib/analytics/loader/gtag";
import GlobalBasketObserver from "@shared/modules/basket/components/GlobalBasketObserver/GlobalBasketObserver.web";
import { setAxiosDefaults } from "@shared/services/axios/utils";
import { TimerProvider } from "@ui-kit/feedback/timer";
import Cookies from "js-cookie";
import moment from "moment";
import "moment/locale/fr";
import { NextPage } from "next";
import { appWithTranslation } from "next-i18next";
import App, { AppContext, AppProps } from "next/app";
import Router, { useRouter } from "next/router";
import Script from "next/script";
import nprogress from "nprogress";
import type { ReactElement, ReactNode } from "react";
import { useEffect } from "react";
import "react-dates/lib/css/_datepicker.css";
import "react-phone-input-2/lib/bootstrap.css";
import { Hydrate } from "react-query/hydration";
import smoothscroll from "smoothscroll-polyfill";

import ThemeProvider from "@/ThemeProvider";
import { PageThemeConfigProvider } from "@/providers/PageThemeConfigContext";
import { usePublicBasket } from "@app/core/src/app-contexts/public/features/basket/services/usePublicBasket";
import ErrorAppCrash from "@app/core/src/shared/components/application/other/ErrorAppCrash/ErrorAppCrash.web";
import ErrorBoundary from "@app/core/src/shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import { BasketServiceProvider } from "@app/core/src/shared/modules/basket/context/BasketServiceContext";
import { WaffleProvider } from "@shared/contexts/WaffleContext/WaffleContext";
import DatadogRUM from "@app/core/src/shared/components/datadog/datadog-rum";
import DatadogWebVitals from "@app/core/src/shared/components/datadog/datadog-web-vitals";
import DatadogUserContext from "@app/core/src/shared/components/datadog/datadog-user-context";

import {
  queryClient,
  QueryClientProvider,
} from "@app/core/src/shared/services/api/utils/GlobalQueryClient";
import { GlobalAuthorizationProvider } from "@app/core/src/shared/modules/auth/contexts/GlobalAuthorizationProvider/GlobalAuthorizationProvider";

// Client-side emotion cache (created once, after all imports)
const clientSideEmotionCache = createEmotionCache();

setAxiosDefaults();

if (typeof window !== "undefined") {
  smoothscroll.polyfill();
}

export type Layout = {
  getLayout?: (page: ReactElement) => ReactNode;
};
export type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  emotionCache?: EmotionCache;
  nonce?: string;
};

const PageWrapper = styled.div`
  flex: 1;
`;

const MyApp = ({
  Component,
  // Temporary workaround for issue https://github.com/vercel/next.js/issues/40372
  pageProps: props,
  emotionCache = clientSideEmotionCache,
  nonce,
}: AppPropsWithLayout): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageProps: any = props;
  const router = useRouter();

  // Initialize TWA detection hook
  useAndroidTWACheck();

  // initial set a locale based on router.locale so web-app knows what locale to use,
  // default is English locale
  const { locale } = router;
  moment.locale(locale || "en");
  Cookies.set("NEXT_LOCALE", locale || "en");

  // Nprogress route changing loading bar
  useEffect(() => {
    if (typeof window === undefined) {
      return;
    }

    const delay = 500; // don't show loading bar unless route change takes longer than delay
    let timer: number;

    const load = () => {
      timer = window.setTimeout(() => {
        nprogress.start();
      }, delay);
    };

    const stop = () => {
      clearTimeout(timer);
      nprogress.done();
    };

    Router.events.on("routeChangeStart", () => load());
    Router.events.on("routeChangeComplete", () => stop());
    Router.events.on("routeChangeError", () => stop());
  }, []);

  // Use the layout defined at the page level, if available
  const getLayout =
    Component.getLayout ||
    ((page) => {
      return (
        <>
          <NavBar />
          <PageWrapper>{page}</PageWrapper>
          <Footer />
        </>
      );
    });

  return (
    <CacheProvider value={emotionCache}>
      <Script
        strategy="beforeInteractive"
        src={`${API_URL}wafflejs`}
        crossOrigin="use-credentials"
      />
      <Script
        strategy="beforeInteractive"
        src={`${SITE_URL}${STATIC_FILE_PATH}polyfills/structuredClone.js`}
      />
      <Script
        strategy="beforeInteractive"
        src={`${SITE_URL}${STATIC_FILE_PATH}polyfills/BroadcastChannel.js`}
      />

      <ErrorBoundary fallback={<ErrorAppCrash />}>
        <DesktopAgentProvider>
          <BasketServiceProvider basketService={usePublicBasket}>
            <WaffleProvider>
              <AnalyticsContextProvider enableAnalytics>
                <PageThemeConfigProvider>
                  <ThemeProvider nonce={nonce}>
                    <DatadogRUM />
                    <DatadogWebVitals />
                    <QueryClientProvider client={queryClient}>
                      {/* Hydrate MUST be the first child of QueryClientProvider that makes use of react-query.
                          When this is not the case, then components (UserSessionAuthStateProvider) access the query cache
                          before it has been hydrated. This leads to mismatches between server-rendered HTML and 
                          client-side rendering.  */}
                      <Hydrate state={pageProps.dehydratedState}>
                        <TimerProvider>
                          <UserSessionAuthStateProvider>
                            <DatadogUserContext />
                            <GlobalAuthorizationProvider>
                              <GlobalBasketObserver />
                              <Template>
                                {getLayout(<Component {...pageProps} />)}
                              </Template>
                            </GlobalAuthorizationProvider>
                          </UserSessionAuthStateProvider>
                        </TimerProvider>
                      </Hydrate>
                    </QueryClientProvider>
                  </ThemeProvider>
                </PageThemeConfigProvider>
              </AnalyticsContextProvider>
            </WaffleProvider>
          </BasketServiceProvider>
        </DesktopAgentProvider>
      </ErrorBoundary>
    </CacheProvider>
  );
};

// Get nonce from request headers for CSP compliance
MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const nonce = appContext.ctx.req?.headers["x-nonce"] as string | undefined;
  return { ...appProps, nonce };
};

export default appWithTranslation(MyApp);
